<script lang="ts">
	import { ParaglideJS } from '@inlang/paraglide-sveltekit';
	import { CircleX } from 'lucide-svelte';
	import { ModeWatcher } from 'mode-watcher';
	import SvelteSeo from 'svelte-seo';

	import { browser } from '$app/environment';
	import { goto, onNavigate } from '$app/navigation';
	import { page } from '$app/state';
	import { onMount, tick } from 'svelte';

	import { AppSidebar } from '$lib/components/app-sidebar';
	import LoadingSpinner from '$lib/components/loading-spinner.svelte';
	import Logo from '$lib/components/logo.svelte';
	import RepoErrorAlert from '$lib/components/repo-error-alert.svelte';
	import * as Alert from '$lib/components/ui/alert/index';
	import { ScrollArea } from '$lib/components/ui/scroll-area';
	import { Separator } from '$lib/components/ui/separator';
	import * as Sidebar from '$lib/components/ui/sidebar';
	import { Toaster } from '$lib/components/ui/sonner';

	import { Bench } from '$lib/bench';
	import { production } from '$lib/constants';
	import { i18n } from '$lib/i18n';
	import * as m from '$lib/paraglide/messages';
	import { clientRepo } from '$lib/states/client-repo.svelte';
	import '$lib/styles/app.css';

	import { getLaunchIntoChats, preloadFrequentRoutes } from '.';
	import Breadcrumb from './breadcrumb.svelte';
	import ClientRepoStatus from './client-repo-status.svelte';
	import Head from './head.svelte';
	import ModeToggle from './mode-toggle.svelte';

	let { data, children } = $props();

	const shouldLaunchIntoChats = getLaunchIntoChats();

	const hideDatabaseError = $derived(page.url.pathname === '/settings/data');

	onMount(() => {
		Bench.measure('sveltekit.on-mount', 'sveltekit.hooks.client');
		setTimeout(preloadFrequentRoutes, 5000);
	});

	$effect(() => {
		if (shouldLaunchIntoChats && clientRepo.repo) {
			tick().then(() => goto('/collections/chats'));
		}
	});

	onNavigate((navigation) => {
		if (!browser || !globalThis.document?.startViewTransition) {
			return;
		}

		return new Promise((resolve) => {
			globalThis.document.startViewTransition(async () => {
				resolve();
				await navigation.complete;
			});
		});
	});

	const title = $derived.by(() => {
		const lastCrumb = page.data.breadcrumbs.at(-1);
		if (lastCrumb) {
			return `${lastCrumb.title} — ${m.productName()}`;
		} else {
			return m.productName();
		}
	});
</script>

<Head />

<ParaglideJS {i18n}>
	<SvelteSeo
		{title}
		applicationName={m.productName()}
		description={m.metaDescription()}
		noindex={!production}
		nofollow={!production}
		manifest="/site.webmanifest"
	/>

	<ModeWatcher />
	<Toaster />

	<Sidebar.Provider>
		<AppSidebar recentChats={data.recentChats} hasChangelogUpdates={data.hasChangelogUpdates} />

		<Sidebar.Inset>
			<header
				class="flex h-16 shrink-0 items-center gap-2 border-b border-border/50 px-4 shadow-lg"
			>
				<Sidebar.Trigger
					side="left"
					class="hidden md:flex text-muted-foreground"
					title="Toggle sidebar (Ctrl + B)"
				/>

				<Separator orientation="vertical" class="mx-2 hidden md:flex bg-border/10" />

				<a href="/" class="flex items-center gap-2 md:hidden">
					<div class="bg-primary flex aspect-square size-8 items-center justify-center rounded-lg">
						<Logo class="fill-sidebar-primary-foreground" />
					</div>

					<div class="flex items-center justify-between w-full">
						<span class="font-semibold text-lg">
							{m.productName()}
						</span>
					</div>
				</a>

				<Separator orientation="vertical" class="mx-2 md:hidden bg-border/10" />

				<div class="flex justify-center sm:justify-start mx-auto md:w-full">
					<Breadcrumb />
				</div>

				<Separator orientation="vertical" class="mx-2 bg-border/10" />

				<ClientRepoStatus />
				<ModeToggle />
				<Sidebar.Trigger side="right" class="md:hidden text-muted-foreground" />
			</header>

			<div class="grid relative h-[calc(100dvh-4rem)]">
				<ScrollArea orientation="vertical">
					<div class="px-4 py-8 relative h-full max-w-7xl mx-auto">
						<noscript>
							<Alert.Root class="mb-8" variant="destructive" aria-hidden="true">
								<CircleX class="size-4" />
								<Alert.Title>{m.noScript_title()}</Alert.Title>
								<Alert.Description>
									{m.noScript_description({ productName: m.productName() })}
								</Alert.Description>
							</Alert.Root>
						</noscript>

						<div class="flex flex-col items-center">
							{#if !clientRepo.repo && !clientRepo.error}
								<span class="flex items-center gap-1.5 h-11">
									<LoadingSpinner />
									<span class="muted"> setting things up... </span>
								</span>
							{/if}

							{#if !hideDatabaseError && clientRepo.error}
								<RepoErrorAlert />
							{/if}
						</div>

						{@render children()}
					</div>
				</ScrollArea>
			</div>
		</Sidebar.Inset>
	</Sidebar.Provider>
</ParaglideJS>
